import { Col, Form, Row, Select } from 'antd'
import { UploadFile } from 'antd/es/upload'
import { useEffect, useState } from 'react'
import { envApiUrl } from '../../../apis/axiosClient'
import { Alert, ResetToken } from '../../../assets/icons'
import { ButtonCommon, IconWrap, InputCommon, ModalCommon, RequireTag, SelectCommon } from '../../../components'
import { UploadImage } from '../../../components/UploadImage'
import { openNotificationWithIcon, useGetAllContracts } from '../../../hooks'
import { useUpdateAdmin } from '../../../hooks/query/manager'
import { useGenToken } from '../../../hooks/query/user'
import { regex } from '../../../until'
import styles from './index.module.scss'
import { useTranslation } from 'react-i18next'

interface IProps {
  title: string
  visible: boolean
  onOk: () => void
  onCancel: () => void
  manager?: any
  id?: string
  isLoading?: boolean
  listErr?: any

  // eslint-disable-next-line no-unused-vars
  handleOnFinish?: (value: any) => void
}

export const ManagerModal = ({
  title,
  visible,
  onOk,
  onCancel,
  manager,
  handleOnFinish,
  isLoading,
  listErr,
}: IProps) => {
  const [avatar, setAvatar] = useState<UploadFile>()
  const mutateUpdateManager = useUpdateAdmin()
  const resetToken = useGenToken()
  const [isResetToken, setIsResetToken] = useState(false)
  const {t,i18n} = useTranslation()

  const { data } = useGetAllContracts()
  const [form] = Form.useForm()
  const onFinish = async (value: any) => {
    const body = {
      ...value,
      use_two_step_authentication: value.use_two_step_authentication === 1
    }
    if (manager) {
      try {
        const formData = new FormData()
        for (const key in body) {
          formData.append(key, body[key])
        }
        formData.append('avatar', avatar as any)
        await mutateUpdateManager.mutateAsync({ id: manager.id, formData: formData })
        onOk()
        openNotificationWithIcon('success', '', `${t('manager.message.updateSuccess')}`)
      } catch (error: any) {
        if (error.errors) {
          const entries = Object.entries(error.errors)
          const arrError = entries.map((item: any) => ({ name: item[0], errors: item[1] }))
          form.setFields(arrError)
        } else {
          openNotificationWithIcon('error', '', error?.message)
        }
      }
    } else {
      body.user_type = '1'
      if (avatar) {
        handleOnFinish?.({ ...body, avatar: avatar })
      } else {
        handleOnFinish?.({ ...body })
      }
    }
  }

  const onResetToken = async () => {
    try {
      await resetToken.mutateAsync(manager.id)
      openNotificationWithIcon('success', `${t('manager.message.resetSuccess')}`)
    } catch (error: any) {
      openNotificationWithIcon('error', error?.message)
    }
    setIsResetToken(false)
  }
  useEffect(() => {
    if (listErr && listErr.length > 0) {
      form.setFields(listErr)
    }
  }, [listErr])

  useEffect(() => {
    if (manager) {
      form.setFieldsValue({
        full_name: manager.full_name || '',
        full_name_katakana: manager.full_name_katakana || '',
        username: manager.username || '',
        number_phone: manager.number_phone || '',
        user_type: manager.user_type || '',
        email: manager.email || '',
        contract_id: manager.contract_code || '',
        address_1: manager.address_1 || '',
        address_2: manager.address_2 || '',
        status: manager.status || '',
        postal_code: manager.postal_code || '',
        building_ids: manager.building_ids,
        use_two_step_authentication: manager?.use_two_step_authentication ? 1 : 0
      })

      return
    }
  }, [])

  return (
    <>
      <ModalCommon
        destroyOnClose={true}
        title={manager ? t('manager.titleDefault') : title}
        visible={visible}
        onOk={onOk}
        onCancel={onCancel}
        width="850px"
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div className={styles.form__container}>
            <div className={styles.form__top}>
              <div className={styles.form__uploadImage}>
                <UploadImage
                  title={t('manager.profileImage')}
                  hasBorder={true}
                  url={manager?.url_avatar ? `${envApiUrl}${manager?.url_avatar}` : ''}
                  onChangeAvatar={setAvatar}
                />
              </div>
              <div className={styles.form__name}>
                <Form.Item
                  label={
                    <>
                      {t('manager.fullName')}
                      <RequireTag />
                    </>
                  }
                  name="full_name"
                  className={styles['form__item--half']}
                  rules={[
                    {
                      required: true,
                      message: t('manager.message.inputFullName'),
                    },
                  ]}
                  initialValue={''}
                  validateTrigger="onSubmit"
                >
                  <InputCommon
                    maxLength={50}
                    placeholder={t('manager.placeholderName')}
                    onBlur={() => {
                      form.setFieldValue('full_name', form.getFieldValue('full_name')?.trim())
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label={
                    <>
                      {t('manager.userName')}
                      <RequireTag />
                    </>
                  }
                  name="username"
                  className={styles['form__item--half']}
                  rules={[
                    {
                      required: true,
                      message: t('manager.message.inputUserName'),
                    },
                    {
                      validator(_, value) {
                        if (value.indexOf(' ') > 0) {
                          return Promise.reject(`${t('manager.message.whiteSpace')}`)
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                  initialValue={''}
                  validateTrigger="onSubmit"
                >
                  <InputCommon
                    maxLength={50}
                    onBlur={() => {
                      form.setFieldValue('username', form.getFieldValue('username')?.trim())
                    }}
                    placeholder={t('manager.placeholderUserName')}
                  />
                </Form.Item>
              </div>
              <div className={styles.form__name}>
                <Form.Item
                  label={t('manager.fullNameKatakana')}
                  name="full_name_katakana"
                  className={styles['form__item--half']}
                  initialValue={''}
                >
                  <InputCommon
                    maxLength={50}
                    onBlur={() => {
                      form.setFieldValue('full_name_katakana', form.getFieldValue('full_name_katakana')?.trim())
                    }}
                    placeholder={t('manager.placeholderNameKatakana')}
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <>
                      {t('manager.contract')}
                      {manager ? null : <RequireTag />}
                    </>
                  }
                  name="contract_id"
                  className={styles['form__item--half']}
                  rules={[
                    {
                      required: true,
                      message: t('manager.message.selectContract'),
                    },
                  ]}
                  validateTrigger="onSubmit"
                >
                  <SelectCommon
                    autoFocus={false}
                    maxTagCount="responsive"
                    disabled={!!manager}
                    placeholder={<p className={styles.form__item__placeholder}>{t('manager.placeholderContract')}</p>}
                  >
                    {data &&
                      data.length > 0 &&
                      data
                        .filter((item: any) => item.status === false)
                        .map((item: any) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.contract_code}
                          </Select.Option>
                        ))}
                  </SelectCommon>
                </Form.Item>
              </div>
            </div>
            <div className={styles.form__bottom}>
              <Form.Item
                label={<>{t('manager.phone')}</>}
                rules={[
                  {
                    pattern: regex.validatePhone,
                    message: t('manager.message.invalidPhone'),
                  },
                ]}
                name="number_phone"
                className={styles['form__item--half']}
                validateTrigger="onSubmit"
                initialValue={''}
              >
                <InputCommon
                  maxLength={11}
                  onBlur={() => {
                    form.setFieldValue('number_phone', form.getFieldValue('number_phone')?.trim())
                  }}
                  placeholder={t('manager.placeholderPhone')}
                />
              </Form.Item>

              <Form.Item
                label={
                  <>
                    {t('manager.email')}
                    {manager ? null : <RequireTag />}
                  </>
                }
                rules={[
                  {
                    required: true,
                    message: t('manager.message.inputEmail'),
                  },
                  {
                    pattern: regex.validateEmail,
                    message: t('manager.message.invalidEmail'),
                  },
                ]}
                name="email"
                className={styles['form__item--half']}
                initialValue={''}
                validateTrigger="onSubmit"
              >
                <InputCommon maxLength={150} placeholder={t('manager.placeholderEmail')} disabled={!!manager} />
              </Form.Item>
              <Form.Item
                label={<>{t('manager.address1')}</>}
                name="address_1"
                className={styles['form__item--full']}
                initialValue={''}
              >
                <InputCommon
                  maxLength={50}
                  onBlur={() => {
                    form.setFieldValue('address_1', form.getFieldValue('address_1')?.trim())
                  }}
                  placeholder={t('manager.placeholderAddress')}
                />
              </Form.Item>
              <Form.Item
                label={t('manager.address2')}
                name="address_2"
                className={styles['form__item--full']}
                initialValue={''}
              >
                <InputCommon
                  maxLength={50}
                  onBlur={() => {
                    form.setFieldValue('address_2', form.getFieldValue('address_2')?.trim())
                  }}
                  placeholder={t('manager.placeholderAddress')}
                />
              </Form.Item>
              <Form.Item
                label={<>{t('manager.postalCode')}</>}
                rules={[
                  {
                    pattern: regex.validatePostalCode,
                    message: t('manager.message.invalidPostalCode'),
                  },
                ]}
                initialValue={''}
                name="postal_code"
                className={styles['form__item--half']}
                validateTrigger="onSubmit"
              >
                <InputCommon
                  maxLength={8}
                  onBlur={() => {
                    form.setFieldValue('postal_code', form.getFieldValue('postal_code')?.trim())
                  }}
                  placeholder={t('manager.placeholderPostalCode')}
                />
              </Form.Item>
              <Form.Item
                label={
                  <>
                    {t('manager.status')}
                    {manager ? null : <RequireTag />}
                  </>
                }
                name="status"
                className={styles['form__item--half']}
                // initialValue={'1'}
                rules={[{ required: true, message: t('manager.message.selectStatus') }]}
                validateTrigger="onSubmit"
              >
                <SelectCommon autoFocus={false} placeholder={t('manager.placeholderStatus')}>
                  <Select.Option value="1">{t('manager.active')}</Select.Option>
                  <Select.Option value="0">{t('manager.inactive')}</Select.Option>
                </SelectCommon>
              </Form.Item>
            </div>
            <Row gutter={[40, 0]}>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                <Form.Item
                  label={
                    <>
                      <strong>{t('staff.addModal.twoFactorAuthentication')}</strong>
                      {manager ? null : <RequireTag />}
                    </>
                  }
                  rules={[
                    {
                      required: true,
                      message: t('staff.addModal.twoFactorAuthentication') + ' ' + t('manager.message.isRequired'),
                    },
                  ]}
                  name="use_two_step_authentication"
                  className={styles['form__item--half']}
                  validateTrigger="onSubmit"
                >
                  <SelectCommon
                    autoFocus={false}
                    maxTagCount="responsive"
                    placeholder={t('staff.addModal.twoFactorAuthenticationPlaceholder')}
                  >
                    <Select.Option value={1}>{t('staff.addModal.active')}</Select.Option>
                    <Select.Option value={0}>{t('staff.addModal.inactive')}</Select.Option>
                  </SelectCommon>
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} sm={{ span: 12 }}>
                {manager && <Form.Item
                  label={
                    <strong>
                      {t('manager.accessToken')}
                    </strong>
                  }
                  className={styles['form__item--full']}
                >
                  <div style={{ border: '1px solid #dddde3', borderRadius: '20px', background: '#f5f5f5' }}>
                    <InputCommon value={manager?.token} style={{ border: '0px', width: '90%' }} disabled />
                    <IconWrap
                      icon={<ResetToken />}
                      className={styles.form__item__edit}
                      onClick={() => setIsResetToken(true)}
                    />
                  </div>
                </Form.Item>}
              </Col>
            </Row>
          </div>
          <div className={styles.form__footer}>
            <ButtonCommon type="ghost" onClick={onCancel}>
              {t('manager.cancel')}
            </ButtonCommon>
            <ButtonCommon
              type="primary"
              htmlType="submit"
              lang={i18n.language}
              loading={isLoading || mutateUpdateManager.isLoading 
              }
            >
              {t('manager.save')}
            </ButtonCommon>
          </div>
        </Form>
      </ModalCommon>
      {
        isResetToken &&
        <ModalCommon
          destroyOnClose={true}
          title={t('manager.titleConfirmReset')}
          visible={isResetToken}
          onOk={onResetToken}
          onCancel={() => setIsResetToken(false)}
          width="500px"
        >
          <div>
            <div className={styles.alertContent}>
              <Alert />
              <p>{t('manager.confirmReset')}</p>
            </div>
            <div className={styles.form__footer}>
              <ButtonCommon type="ghost" onClick={() => setIsResetToken(false)}>
                {t('manager.cancel')}
              </ButtonCommon>
              <ButtonCommon
                type="primary"
                onClick={onResetToken}
                lang={i18n.language}
              >
                {t('manager.save')}
              </ButtonCommon>
            </div>
          </div>
        </ModalCommon>
      }
    </>
  )
}
